<script>
// Alerts
// import { simpleWarning } from '@utils/alerts'
import { simpleWarning } from '@utils/alerts'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { baseMethods } from '@state/helpers/base.js'

export default {
    components: {
        VueCropper,
    },
    props: {
        isRounded: {
            type: Boolean,
            default: false,
        },
        maxSize: {
            type: [Number, String],
            default: 52428800,
        },
        className: {
            type: String,
            default: '',
        },
        accept: {
            type: String,
            default: '',
        },
        fixedSize: {
            type: Boolean,
            default: false,
        },
        cropper: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            file: '',
            imageData: '',
            dragging: false,
            openCropper: false,
            croppedImage: '',
        }
    },
    computed: {
        isImageFile() {
            // retorna true para imagens cortadas
            if (!this.file.name) return true
            const imageExtensions = ['jpg', 'jpeg', 'png', 'svg']
            const fileExtension = this.file.name.split('.').pop()
            return imageExtensions.some((ext) => ext === fileExtension)
        },
        getIcon() {
            const fileExtension = this.file.name.split('.').pop()
            const icons = {
                xls: 'excel',
                ppt: 'powerPoint',
                pdf: 'pdf',
                text: 'text',
                txt: 'text',
                docx: 'text',
            }
            return `icn_${icons[fileExtension] || 'file'}`
        },
        isDragOver() {
            return this.dragging && !this.file
        },
    },
    methods: {
        ...baseMethods,
        onFileDrop(e) {
            this.draggin = false
            const droppedFile = e.dataTransfer.files[0]
            this.file = droppedFile
            const validated = this.validateFile(e)
            if (!validated) {
                this.file = ''
                return simpleWarning('noImage')
            }
            if (validated) {
                if (this.isImageFile) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        this.imageData = e.target.result
                    }
                    reader.readAsDataURL(this.file)
                } else {
                    this.imageData = ''
                }
                this.$emit('file-uploaded', this.file)
                this.openCropper = this.cropper
                this.setOverlay(this.cropper)
            }
        },
        onFileChange(e) {
            const file = e.target.files[0]
            if (!file) return
            // if (file.size > this.maxSize) return simpleWarning('maxSize')
            this.file = file
            const validated = this.validateFile(e)
            if (!validated) {
                return (this.file = '')
            }
            if (validated) {
                if (this.isImageFile) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        this.imageData = e.target.result
                    }
                    reader.readAsDataURL(this.file)
                } else {
                    this.imageData = ''
                }
                this.$emit('file-uploaded', this.file)
                this.openCropper = this.cropper
                this.setOverlay(this.cropper)
            }
        },
        validateFile() {
            if (this.file.size > this.maxSize) {
                simpleWarning('maxSize')
                return false
            }
            if (this.accept) {
                const fileValidations = {
                    image: this.isImageFile,
                }
                if (!fileValidations[this.accept]) {
                    simpleWarning('noImage')
                    return false
                }
            }
            return true
        },
        reset() {
            this.file = ''
            this.imageData = ''
            if (this.$refs.uploader) this.$refs.uploader.value = ''
            this.dragging = false
            this.$emit('reset')
        },
        resetCrop() {
            this.$refs.cropper.reset()
        },
        cropImage() {
            this.imageData = this.$refs.cropper.getCroppedCanvas().toDataURL()
            this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
                this.file = blob
                this.$emit('file-uploaded', this.file)
                this.$nextTick().then(() => this.$emit('cropped-image'))
            })
            this.openCropper = false
            this.setOverlay(false)
        },
        closeCropper() {
            this.openCropper = false
            this.setOverlay(false)
            this.reset()
        },
        changeImage() {
            this.$refs.uploader.click()
            window.onfocus = this.dissmissInput
        },
        dissmissInput() {
            window.onfocus = null
            // TODO: você, que quando estiver vendo isso eu já não estarei mais cuidando desse projeto,
            // tem a tarefa de encontrar uma solução mais elegante para evitar que caia dentro do if
            // mesmo quando o usuário escolher uma foto. Tentei usar o nextTick pra aguardar a alteração no DOM
            // antes de fazer a verificação, mas não rolou. Boa sorte =)
            setTimeout(() => {
                if (!this.file && !this.openCropper) {
                    this.$emit('return-previous-picture')
                }
            }, 500)
        },
    },
}
</script>

<template>
    <div
        :class="className"
        class="file-uploader"
        @dragover.prevent
        @drop.prevent
    >
        <input
            id="file-uploader"
            ref="uploader"
            class="file-uploader__input"
            name="file-uploader"
            type="file"
            :cropper="false"
            :accept="accept"
            :multiple="false"
            @change="onFileChange"
        />
        <label
            class="file-uploader__label"
            for="file-uploader"
            @drop="onFileDrop"
        >
            <div
                :class="[
                    { dragged: isDragOver },
                    { 'rounded-uploader': isRounded },
                ]"
                class="file-uploader__label-container"
                @dragover="dragging = true"
                @dragleave="dragging = false"
            >
                <div
                    v-if="file"
                    class="file-uploader__label-close"
                    @click.prevent="reset"
                >
                    <svgicon
                        width="15"
                        color="#ff0000"
                        height="15"
                        icon="icn_error2"
                    />
                </div>
                <b-container>
                    <div v-if="!file">
                        <template v-if="!isRounded">
                            <b-row>
                                <b-col class="text-center">
                                    <p>{{ $t('Rightpanel.dragFile') }}</p>
                                    <p>{{ $t('Rightpanel.selectFile[0]') }}</p>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col class="text-center">
                                    <span class="file-uploader__label-button">
                                        {{ $t('Rightpanel.selectFile[1]') }}
                                    </span>
                                </b-col>
                            </b-row>
                        </template>
                        <template v-else>
                            <b-row>
                                <b-col
                                    class=" d-flex flex-column align-items-center"
                                >
                                    <svgicon
                                        icon="icn_empty-uploader"
                                        color="#e0e1e2"
                                        width="50"
                                        height="50"
                                    />
                                    <span
                                        class="file-uploader__label-picture mt-1"
                                        >{{ $t('Buttons.choosePhoto') }}</span
                                    >
                                </b-col>
                            </b-row>
                        </template>
                    </div>
                    <div
                        v-else
                        class="file-uploader__label-thumbnail"
                        :class="{ 'rounded-thumb': isRounded }"
                        :style="{
                            'background-image': `url(${imageData})`,
                        }"
                    >
                        <template v-if="file && !isImageFile">
                            <svgicon width="100" height="100" :icon="getIcon" />
                            <span class="mt-1">
                                {{ file.name }}
                            </span>
                        </template>
                    </div>
                </b-container>
            </div>
        </label>
        <div v-if="cropper && openCropper" class="file-uploader__cropper">
            <div class="file-uploader__cropper--close">
                <mgb-button
                    icon="icn_close-simple"
                    button-class="btn__close"
                    @click.prevent="closeCropper"
                />
            </div>
            <div class="file-uploader__cropper--title">
                <h4>Ajuste sua foto antes de salvar</h4>
            </div>
            <div v-if="imageData" class="file-uploader__cropper--container">
                <div class="file-uploader__cropper--image">
                    <VueCropper
                        ref="cropper"
                        :src="imageData"
                        :aspect-ratio="1 / 1"
                        :zoomable="false"
                    />
                </div>
            </div>
            <div class="file-uploader__cropper--buttons mt-1">
                <mgb-button
                    class="new-form-button mt-1"
                    variant="success"
                    @click.prevent="cropImage"
                >
                    {{ $t('Buttons.cutAndSave') }}
                </mgb-button>
                <mgb-button variant="primary" @click.prevent="resetCrop">
                    {{ $t('Buttons.reset') }}
                </mgb-button>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@src/design/components/file-uploader';
</style>
